import React from 'react';
import { Box, Container, Typography, Stack, Link, IconButton, Divider } from '@mui/material';
import { Facebook, Instagram } from '@mui/icons-material';
import { useLanguage } from '../hooks/useLanguage';
import { getLocalizedLegalPath } from '../utils/legalLinks';

interface PublicFooterProps {
  onOpenAbout?: () => void;
  onOpenContact?: () => void;
  onOpenPricing?: () => void;
}

const PublicFooter: React.FC<PublicFooterProps> = ({ onOpenAbout, onOpenContact, onOpenPricing: _onOpenPricing }) => {
  const year = new Date().getFullYear();
  const { t, currentLanguage } = useLanguage();
  const privacyPath = getLocalizedLegalPath('privacy', currentLanguage);
  const termsPath = getLocalizedLegalPath('terms', currentLanguage);
  const impressumPath = getLocalizedLegalPath('impressum', currentLanguage);

  return (
    <Box component="footer" sx={{ bgcolor: 'background.paper', borderTop: (theme) => `1px solid ${theme.palette.divider}`, mt: 6 }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Stack spacing={1}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t('appName')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('appTagline')}</Typography>
          </Stack>

          <Stack direction="row" spacing={3}>
            <Link href="#" underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); onOpenAbout?.(); }}>{t('footerLinks.about')}</Link>
            <Link href={termsPath} underline="hover" color="inherit">{t('footerLinks.terms')}</Link>
            <Link href={privacyPath} underline="hover" color="inherit">{t('footerLinks.privacy')}</Link>
            <Link href={impressumPath} underline="hover" color="inherit">{t('footerLinks.impressum')}</Link>
            {/* Pricing temporarily hidden - website is not commercial yet */}
            {/* <Link href="#" underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); onOpenPricing?.(); }}>{t('navigation.pricing')}</Link> */}
            <Link href="#" underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); onOpenContact?.(); }}>{t('footerLinks.contact')}</Link>
          </Stack>

          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              aria-label="Facebook"
              color="inherit"
              component="a"
              href="https://www.facebook.com/profile.php?id=61578929398683"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Facebook fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Instagram"
              color="inherit"
              component="a"
              href="https://www.instagram.com/treffpunkts/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Instagram fontSize="small" />
            </IconButton>
            {/** Twitter and YouTube not available yet - keep hidden for now for SEO clarity */}
            {/** <IconButton size="small" aria-label="Twitter" color="inherit"><Twitter fontSize="small" /></IconButton> */}
            {/** <IconButton size="small" aria-label="YouTube" color="inherit"><YouTube fontSize="small" /></IconButton> */}
          </Stack>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="caption" color="text.secondary">
          {t('footer.copyright', { year })} {t('footer.ctoCredit')}
        </Typography>
      </Container>
    </Box>
  );
};

export default PublicFooter;

